import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import {
  Notification,
  Room,
  Roomgroup,
  Building,
  BusinessEntity,
} from '@/models';
import { floorLevelList, floorLevelListMapping } from '@/enums/room';
import useStores from '@/composables/useStores';
import { useBuildingCard } from '@/composables/useBuildingCard';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { useOrderStore } from '@/store/order/orderStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { useFunkcheckStore } from '@/store/funkcheck/funkcheckStore';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { Tag } from '@/utils/canvas/drawingUtils';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { DeviceTypeEnum } from '@/enums/device';
import { useRouter } from 'vue-router';
import { Form } from '@/models/Form';
import type { OrderAppointment } from '@/models/types/Order';
import { Status, entityTypes } from '@/enums/generic';

export interface DeviceTags {
  ACTIVE: Array<Tag>;
  INACTIVE: Array<Tag>;
  UNMOUNTED: Array<Tag>;
}

export interface BuildingFloorData {
  entrance?: Roomgroup;
  roomgroups: Roomgroup[];
}

export interface BuildingStructure {
  [floorName: string]: BuildingFloorData;
}

export interface RoomGroupDataMaps {
  deviceTags: Map<string, DeviceTags>;
  activeInstallationPoints: Map<string, Array<any>>;
  unmountedDevices: Map<string, Array<any>>;
  noSignalDevices: Map<string, Array<any>>;
  isInstallationLocation: Map<string, boolean>;
  activeInspectionGateways: Map<string, Array<any>>;
  appointmentByRoomGroupId: Map<string, OrderAppointment>;
  unfinishedRoomGroups: Map<string, Roomgroup>;
  roomGroupProgress: Map<string, { isComplete: boolean; label: string }>;
  isRoomgroupDeletable: Map<string, boolean>;
  hasStaircaseTestDevices: Map<string, boolean>;
  funkcheckErrorCount: Map<string, number>;
}

export interface RoomGroupHandlers {
  delete: (roomGroup: Roomgroup) => void;
  edit: (roomGroup: Roomgroup, options?: { scrollToBottom?: boolean }) => void;
  enter: (roomGroup: Roomgroup) => void;
  toggleProgress: (roomGroup: Roomgroup) => void;
  changeAccessible: (roomGroup: Roomgroup, isAccessible: boolean) => void;
  removeDevices: (roomGroup: Roomgroup) => void;
}

export interface FloorHandlers {
  addRoomgroup: (entranceId: string) => void;
  deleteFloor: (entrance: Roomgroup, floorName: string) => void;
  swapRoomgroups: (source: Roomgroup, target: Roomgroup) => void;
}

export interface UseBuildingOverviewReturn {
  isSwapModeActive: Ref<boolean>;
  activeBuilding: ComputedRef<Building | undefined>;
  buildings: ComputedRef<Building[]>;
  buildingStruct: ComputedRef<BuildingStructure>;
  businessEntity: ComputedRef<BusinessEntity | undefined>;
  orderId: ComputedRef<string | undefined>;
  isOnSiteInspection: ComputedRef<boolean>;
  isFirstInstallationOrder: ComputedRef<boolean>;
  buildingInformation: ComputedRef<BuildingInformation | undefined>;
  unfinishedRoomGroups: ComputedRef<Map<string, Roomgroup>>;
  isDarkMode: ComputedRef<boolean>;
  roomGroupDataMaps: ComputedRef<RoomGroupDataMaps>;
  initialize: () => void;
  toggleSwapMode: () => void;
  addFloor: (type: string) => void;
  addBuilding: () => void;
  openInspectedBuildingForm: () => void;
  isHighestFloorLevel: (floorName: string) => boolean;
  provideValues: {
    isSwapModeActive: Ref<boolean>;
    changeActiveBuilding: (building: Building) => void;
    editBuilding: (
      building: Building,
      options?: { scrollToBottom?: boolean }
    ) => void;
    handleIsBuildingCompatible: (building: Building) => void;
    roomGroupHandlers: RoomGroupHandlers;
    floorHandlers: FloorHandlers;
    isDarkMode: ComputedRef<boolean>;
  };
}

export interface BuildingInformation {
  message: string;
  type: Status.INFO | Status.WARNING;
}

export function useBuildingOverview(): UseBuildingOverviewReturn {
  const {
    roomStore,
    entitiesStore,
    roomGroupStore,
    buildingStore,
    formStore,
    businessEntityStore,
    orderStore,
    inspectionPointStore,
  } = useStores();

  const installationPointStore = useInstallationPointStore();
  const funkcheckStore = useFunkcheckStore();
  const layoutStore = useLayoutStore();
  const inspectionPointStoreFactory = useInspectionPointStore();
  const router = useRouter();

  const {
    changeActiveBuilding,
    editBuilding,
    handleIsBuildingCompatible,
    isActiveBuilding,
    addBuilding,
  } = useBuildingCard();

  const isSwapModeActive = ref(false);

  const businessEntity = computed(() => businessEntityStore.businessEntity);
  const activeBuilding = computed(() => buildingStore.getCurrentBuilding());
  const buildings = computed(() => buildingStore.sortedBuildings);
  const orderId = computed(() => orderStore.activeOrderId);
  const isOnSiteInspection = computed(() => orderStore.isOnSiteInspection());
  const isFirstInstallationOrder = computed(() =>
    useOrderStore().isFirstInstallationOrder()
  );

  const isDarkMode = computed(() => layoutStore.isDarkMode());

  const getRoomGroups = computed(() => {
    if (activeBuilding.value) {
      return roomGroupStore.getRoomGroupsByBuildingId(activeBuilding.value.id);
    }
    return [];
  });

  const unfinishedRoomGroups = computed(() =>
    orderStore.unfinishedSmartHeatingRoomGroups()
  );

  const sortFloors = (
    obj: Record<string, BuildingFloorData>
  ): BuildingStructure => {
    const newObj: BuildingStructure = {};
    const revObj = Object.keys(obj).sort((e1, e2) => {
      const el1 = { type: e1.split('-')[0], floor: e1.split('-')[1] };
      const el2 = { type: e2.split('-')[0], floor: e2.split('-')[1] };

      if (el1.type === el2.type && el1.type === 'OG') {
        return Number(el2.floor) - Number(el1.floor);
      }

      if (el1.type === el2.type && el1.type === 'UG') {
        return Number(el1.floor) - Number(el2.floor);
      }

      if (el1.type === 'EG' && el2.type === 'UG') {
        return -1;
      }

      if (el1.type === 'OG') {
        return -1;
      }
      return 1;
    });

    revObj.forEach((i: string) => {
      newObj[i] = obj[i];
    });

    return newObj;
  };

  const buildingStruct = computed<BuildingStructure>(() => {
    if (!activeBuilding.value) {
      return {};
    }
    const roomGroups = getRoomGroups.value;
    const buildingStructArray: Record<string, BuildingFloorData> = {};

    roomGroups.forEach((roomgroup) => {
      const firstRoom = roomStore.getFirstRoomOfRoomgroup(roomgroup.id);
      if (!firstRoom) {
        return;
      }
      const floorType = firstRoom.getFloor();
      if (!floorType) return;

      if (!buildingStructArray[floorType]) {
        buildingStructArray[floorType] = {
          roomgroups: [],
        };
      }

      if (
        roomgroup.usageType === 'GENERAL' &&
        firstRoom.usageType === 'HALLWAY_AND_STAIRCASE'
      ) {
        buildingStructArray[floorType].entrance = roomgroup;
      } else {
        buildingStructArray[floorType].roomgroups.push(roomgroup);
      }

      buildingStructArray[floorType].roomgroups.sort((a, b) => {
        return parseInt(String(a.ordinal)) - parseInt(String(b.ordinal));
      });
    });

    return sortFloors(buildingStructArray);
  });

  watch(
    buildingStruct,
    (newStruct) => {
      useBuildingStore().setBuildingStruct(newStruct);
    },
    { immediate: true }
  );

  const roomGroupIds = computed<string[]>(() => {
    if (
      !buildingStruct.value ||
      Object.keys(buildingStruct.value).length === 0
    ) {
      return [];
    }

    const ids: string[] = [];
    for (const floorData of Object.values(buildingStruct.value)) {
      if (floorData.entrance) {
        ids.push(floorData.entrance.id);
      }
      for (const roomgroup of floorData.roomgroups) {
        ids.push(roomgroup.id);
      }
    }
    return ids;
  });

  const roomGroupDataMaps = computed<RoomGroupDataMaps>(() => {
    const deviceTags = new Map<string, DeviceTags>();
    const activeInstallationPoints = new Map<string, Array<any>>();
    const unmountedDevices = new Map<string, Array<any>>();
    const noSignalDevices = new Map<string, Array<any>>();
    const isInstallationLocation = new Map<string, boolean>();
    const activeInspectionGateways = new Map<string, Array<any>>();
    const appointmentByRoomGroupId = new Map<string, OrderAppointment>();
    const roomGroupProgress = new Map<
      string,
      { isComplete: boolean; label: string }
    >();
    const isRoomgroupDeletable = new Map<string, boolean>();
    const hasStaircaseTestDevices = new Map<string, boolean>();
    const funkcheckErrorCount = new Map<string, number>();
    const funkcheckStatuses = useDmgw6Store().deviceFunkcheckStatuses;

    const installerAppointmentBuildings =
      orderStore.activeOrder?.installerAppointment
        ?.installerAppointmentBuildings;
    installerAppointmentBuildings?.forEach((buildingAppointment) => {
      buildingAppointment.installerAppointmentRoomGroups?.forEach(
        (roomGroupAppointment) => {
          if (!roomGroupAppointment.id || !roomGroupAppointment.appointment)
            return;
          appointmentByRoomGroupId.set(
            roomGroupAppointment.id,
            roomGroupAppointment.appointment
          );
        }
      );
    });

    const ids = roomGroupIds.value;
    if (ids.length === 0) {
      return {
        deviceTags,
        activeInstallationPoints,
        unmountedDevices,
        noSignalDevices,
        isInstallationLocation,
        activeInspectionGateways,
        appointmentByRoomGroupId,
        unfinishedRoomGroups: unfinishedRoomGroups.value,
        roomGroupProgress,
        isRoomgroupDeletable,
        hasStaircaseTestDevices,
        funkcheckErrorCount,
      };
    }

    const radioCheckResult = funkcheckStore.loRaWanRadioCheck?.result || null;
    const isOnSite = isOnSiteInspection.value;

    for (const roomGroupId of ids) {
      const inspectionTags =
        inspectionPointStore.getTagsOfRoomgroup(roomGroupId);
      const installationTags =
        installationPointStore.getTagsOfRoomgroup(roomGroupId);
      deviceTags.set(roomGroupId, {
        ACTIVE: [...inspectionTags.ACTIVE, ...installationTags.ACTIVE],
        INACTIVE: [...inspectionTags.INACTIVE, ...installationTags.INACTIVE],
        UNMOUNTED: [...installationTags.UNMOUNTED],
      });

      const inspectionPoints =
        inspectionPointStore.getInspectionPointsByRoomGroupId(roomGroupId);
      const installationPointsForRoomGroup =
        installationPointStore.getInstallationPointsByRoomGroupId(roomGroupId);

      if (isOnSite) {
        activeInstallationPoints.set(
          roomGroupId,
          inspectionPoints.filter((ip: any) => ip.activeTestDeviceId)
        );
      } else {
        activeInstallationPoints.set(
          roomGroupId,
          installationPointsForRoomGroup.filter((ip: any) => ip.activeDeviceId)
        );
      }

      unmountedDevices.set(
        roomGroupId,
        installationPointsForRoomGroup.filter((ip: any) => {
          if (!ip.supportedDeviceType || ip.activeDeviceId) return false;
          return ip.isDeviceUnmounted();
        })
      );

      if (!radioCheckResult) {
        noSignalDevices.set(roomGroupId, []);
      } else {
        noSignalDevices.set(
          roomGroupId,
          installationPointsForRoomGroup.filter(
            (ip: any) =>
              ip.supportedDeviceType === 'SMART_RADIATOR_THERMOSTAT' &&
              ip.activeDeviceId &&
              radioCheckResult[ip.id]?.status !== 'OK'
          )
        );
      }

      const selectedLocations =
        inspectionPointStore.getSelectedLocationsByRoomGroupId(roomGroupId);
      isInstallationLocation.set(roomGroupId, selectedLocations.length > 0);

      activeInspectionGateways.set(
        roomGroupId,
        inspectionPoints.filter(
          (ip: any) => ip.supportedDeviceType === 'LO_RA_WAN_GATEWAY'
        )
      );

      const roomGroup = entitiesStore.getEntityById(roomGroupId) as
        | Roomgroup
        | undefined;
      if (roomGroup) {
        const isComplete = orderStore.isRoomGroupCompleted(roomGroup);
        roomGroupProgress.set(roomGroupId, {
          isComplete: isComplete ?? false,
          label: isComplete ? 'als unfertig markieren' : 'als fertig markieren',
        });

        isRoomgroupDeletable.set(
          roomGroupId,
          roomGroupStore.isRoomgroupEmpty(roomGroup)
        );
      }

      hasStaircaseTestDevices.set(
        roomGroupId,
        inspectionPoints.some((ip: InspectionPoint) => ip.isRemoteTest)
      );

      if (funkcheckStatuses.size > 0) {
        let count = 0;
        for (const room of roomStore.getRoomsByRoomGroupId(roomGroupId)) {
          for (const device of useDeviceStore().getAllDevicesInRoom(room.id)) {
            if (
              funkcheckStatuses.get(device.id) ===
              Dmgw6DeviceFunkcheckStatus.error
            )
              count++;
          }
        }
        if (count > 0) funkcheckErrorCount.set(roomGroupId, count);
      }
    }

    return {
      deviceTags,
      activeInstallationPoints,
      unmountedDevices,
      noSignalDevices,
      isInstallationLocation,
      activeInspectionGateways,
      appointmentByRoomGroupId,
      unfinishedRoomGroups: unfinishedRoomGroups.value,
      roomGroupProgress,
      isRoomgroupDeletable,
      hasStaircaseTestDevices,
      funkcheckErrorCount,
    };
  });

  const hasUnverifiedBuildingInOrder = computed(() => {
    const orderChanges = orderStore.getOrderChanges();
    if (!orderChanges) return false;

    return buildings.value.some((building) =>
      orderChanges.hasUnverifiedEntity(entityTypes.building, building.id)
    );
  });

  const buildingInformation = computed<BuildingInformation | undefined>(() => {
    if (isOnSiteInspection.value && hasUnverifiedBuildingInOrder.value) {
      return {
        message: 'Angaben zu den Gebäuden fehlen. Bitte ergänzen.',
        type: Status.WARNING,
      };
    }

    const activeBuildingId = activeBuilding.value?.id;
    if (!activeBuildingId) return undefined;

    const installationLocations =
      inspectionPointStore.getInCompletedLocationsByBuildingId(activeBuildingId)
        .length || 0;
    const selected =
      inspectionPointStore.getSelectedLocationsByBuildingId(activeBuildingId)
        .length || 0;

    const formatQuantity = (n: number, s1: string, s2: string) =>
      n === 1 ? `1 ${s1}` : `${n} ${s2}`;

    let message: string | undefined;

    if (isOnSiteInspection.value) {
      if (installationLocations > 0 && selected > 0) {
        message = `Du hast ${formatQuantity(installationLocations, 'Installationsort', 'Installationsorte')} vorgeschlagen und bereits ${formatQuantity(selected, 'Installationsort', 'Installationsorte')} ausgewählt.`;
      } else if (installationLocations > 0) {
        message = `Finale Auswahl erforderlich: Du hast ${formatQuantity(installationLocations, 'Installationsort', 'Installationsorte')} vorgeschlagen.`;
      } else {
        message = 'Für die finale Auswahl bitte Installationsorte testen.';
      }
    } else if (
      isFirstInstallationOrder.value &&
      !orderStore.activeOrder?.hasDmgw6Article()
    ) {
      message =
        selected > 0
          ? `Es wurden ${formatQuantity(selected, 'Installationsort', 'Installationsorte')} ausgewählt.`
          : 'Es wurden noch keine Installationsorte ausgewählt.';
    }

    if (!message) return undefined;

    return {
      message,
      type: Status.INFO,
    };
  });

  const initialize = () => {
    const currentBuildingId = buildingStore.buildingId;
    if (!currentBuildingId) {
      const firstBuilding = buildingStore.sortedBuildings[0];
      if (!firstBuilding) return;
      buildingStore.setBuilding(firstBuilding);
    }
  };

  const toggleSwapMode = () => {
    isSwapModeActive.value = !isSwapModeActive.value;
  };

  const addFloor = (type: string) => {
    const roomgroup = new Roomgroup();
    roomgroup.buildingId = activeBuilding.value?.id;
    roomgroup.usageType = 'GENERAL';
    roomgroup.ordinal = 0;

    const room = new Room();
    room.usageType = 'HALLWAY_AND_STAIRCASE';
    room.roomGroupId = roomgroup.id;
    room.ordinal = 0;

    const floors = Object.keys(buildingStruct.value);
    let level = 0;

    if (floors.filter((b) => b.includes(type)).length > 0) {
      level = floors.filter((b) => b.includes(type)).length;
    }

    const currentFloorLevelType = floorLevelList.find(
      (f: any) => f.name === type
    )?.value as keyof typeof floorLevelListMapping | undefined;

    if (currentFloorLevelType) {
      room.floorLevel = {
        type: currentFloorLevelType,
        level: level + 1,
      };
    }

    const floorLevel = level !== 0 ? `${type}-${level}` : 'EG';
    const firstRoom = roomStore.getFirstRoomOfRoomgroup(
      buildingStruct.value[floorLevel]?.entrance?.id || ''
    );
    room.parentRoomId = firstRoom?.id;

    entitiesStore.saveEntity(roomgroup);

    const options = new Notification(
      `Die Etage ${type}-${room.floorLevel.level} wurde hinzugefügt.`,
      'Etage wurde hinzugefügt!',
      'success',
      3000
    );
    entitiesStore.saveEntity(room, options);
  };

  /**
   * Opens the inspected building form dialog
   */
  const openInspectedBuildingForm = () => {
    layoutStore.openFormDialog();
    formStore.setCurrentFormByName('inspectedBuilding', false);
  };

  const handleDeleteRoomgroup = (roomGroup: Roomgroup) => {
    entitiesStore.deleteEntity(
      roomGroup,
      new Notification(
        'Nutzeinheit wurde gelöscht',
        'Nutzeinheit wurde entfernt!',
        'info',
        3000
      )
    );
  };

  const handleEditRoomgroup = (
    roomGroup: Roomgroup,
    options?: { scrollToBottom?: boolean }
  ) => {
    formStore.editEntity(roomGroup, undefined, options);
  };

  const handleEnterRoomgroup = (roomGroup: Roomgroup) => {
    roomGroupStore.setActiveMapRoomGroup(roomGroup.id);
    router.push('/detailansicht');
  };

  const handleToggleProgress = (roomGroup: Roomgroup) => {
    orderStore.toggleRoomGroupProgress(roomGroup);
  };

  const handleChangeAccessible = (
    roomGroup: Roomgroup,
    isAccessible: boolean
  ) => {
    roomGroup.isAccessible = isAccessible;
    if (isAccessible) {
      roomGroup.reasonNotAccessible = undefined;
      roomGroup.dateNotAccessible = undefined;
      roomGroup.typeNotAccessible = undefined;
    } else {
      roomGroup.dateNotAccessible = new Date().toISOString();
    }
    entitiesStore.saveEntity(roomGroup);
  };

  const handleRemoveDevices = (roomGroup: Roomgroup) => {
    const inspectionPoints =
      inspectionPointStoreFactory.getInspectionPointsByRoomGroupId(
        roomGroup.id
      );

    const activeInspectionPoints = inspectionPoints.filter(
      (inspectionPoint: InspectionPoint) =>
        inspectionPoint.activeTestDeviceId &&
        inspectionPoint.supportedDeviceType ===
          DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT
    );

    activeInspectionPoints.forEach((inspectionPoint: InspectionPoint) => {
      const device = inspectionPoint.getActiveDevice();
      entitiesStore.deleteEntity(device);
      inspectionPoint.unsetActiveDevice();
      entitiesStore.saveEntity(inspectionPoint);
    });
  };

  const handleAddRoomgroup = (entranceId: string) => {
    const parentRoom = roomStore.getFirstRoomOfRoomgroup(entranceId);
    if (!parentRoom) return;
    formStore.createEntity(
      new Form().setName('roomgroup').setParentId(parentRoom.id)
    );
  };

  const handleDeleteFloor = (entrance: Roomgroup, floorName: string) => {
    entitiesStore.deleteEntity(
      entrance,
      new Notification(
        'Etage wurde entfernt!',
        `Die Etage ${floorName} wurde entfernt!`,
        'success',
        3000
      )
    );
  };

  const handleSwapRoomgroups = (source: Roomgroup, target: Roomgroup) => {
    useRoomGroupStore().swapRoomGroup(source, target);
  };

  const roomGroupHandlers: RoomGroupHandlers = {
    delete: handleDeleteRoomgroup,
    edit: handleEditRoomgroup,
    enter: handleEnterRoomgroup,
    toggleProgress: handleToggleProgress,
    changeAccessible: handleChangeAccessible,
    removeDevices: handleRemoveDevices,
  };

  const floorHandlers: FloorHandlers = {
    addRoomgroup: handleAddRoomgroup,
    deleteFloor: handleDeleteFloor,
    swapRoomgroups: handleSwapRoomgroups,
  };

  const isHighestFloorLevel = (floorName: string): boolean => {
    const floorLevelKeys = Object.keys(buildingStruct.value);
    const floorLevels = floorLevelKeys
      .filter((floorLevel) => floorLevel.includes(floorName.split('-')[0]))
      .map((floorLevel) => Number(floorLevel.split('-')[1]));
    const highestFloorLevel = Math.max(...floorLevels);
    return floorName.includes(String(highestFloorLevel));
  };

  watch(
    () => buildings.value.length,
    (newBuildingListLength) => {
      const lastBuilding = buildings.value[newBuildingListLength - 1];
      if (lastBuilding) {
        changeActiveBuilding(lastBuilding);
        isActiveBuilding(lastBuilding);
      }
    }
  );

  return {
    isSwapModeActive,
    activeBuilding,
    buildings,
    buildingStruct,
    businessEntity,
    orderId,
    isOnSiteInspection,
    isFirstInstallationOrder,
    buildingInformation,
    unfinishedRoomGroups,
    isDarkMode,
    roomGroupDataMaps,

    initialize,
    toggleSwapMode,
    addFloor,
    addBuilding,
    openInspectedBuildingForm,
    isHighestFloorLevel,

    provideValues: {
      isSwapModeActive,
      changeActiveBuilding,
      editBuilding,
      handleIsBuildingCompatible,
      roomGroupHandlers,
      floorHandlers,
      isDarkMode,
    },
  };
}
